import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PetContextProvider } from "./context/PetContext";
import { PublicAccessProvider } from "./context/PublicAccessContext";
import { PrivacyProvider, CookieConsentBanner, ScriptBlocker } from "./components/PrivacyConsent";
import { LanguageProvider } from "./context/LanguageContext";
import { ClubSettingsProvider } from "./context/ClubSettingsContext";
import { FeatureToggleProvider } from "./context/FeatureToggleContext";
import { ClubBrandingProvider } from "./context/ClubBrandingContext";
import { GuestIdentityProvider } from "./context/GuestIdentityContext";
import { BetaGate } from "./components/BetaGate";
import { Loader2 } from "lucide-react";
import './i18n';
import { installApiErrorInterceptor } from './lib/apiErrorInterceptor';

// Install the global axios interceptor once at module load. This makes
// structured error_code responses from the backend auto-render smart
// toasts (see backend/services/app_errors.py).
installApiErrorInterceptor();

// Route groups
import { ProtectedRoute, VBBProtectedRoute, Layout, useDomainContext } from './routes/AppHelpers';
import { getPublicRoutes } from './routes/publicRoutes';
import { getDiscoverRoutes } from './routes/discoverRoutes';
import { getMemberRoutes } from './routes/memberRoutes';
import { getAdminRoutes } from './routes/adminRoutes';

// Landing pages needed for RootPage logic
import Landing from "./pages/Landing";
import WorldPetsLanding from "./pages/WorldPetsLanding";
import ClubHomepage from "./pages/ClubHomepage";
import ClubNetworkLanding from "./pages/ClubNetworkLanding";

import { BetaFeedbackButton } from "./components/BetaFeedbackButton";
import VersionBadge from "./components/VersionBadge";
import SeoVerificationTags from "./components/SeoVerificationTags";

function AppRoutes() {
  const { user } = useAuth();
  const { domainContext, domainLoading } = useDomainContext();

  // Determine what to show at the root path based on domain
  const RootPage = () => {
    if (domainLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      );
    }

    if (domainContext.type === 'club') {
      if (domainContext.club === 'all-american-dog-club') return <Landing />;
      if (domainContext.club === 'worldpets') return <WorldPetsLanding />;
      return <ClubHomepage />;
    }
    
    return <ClubNetworkLanding />;
  };

  return (
    <ClubBrandingProvider clubSlug={domainContext.club}>
    <FeatureToggleProvider clubSlug={domainContext.club}>
    <Routes>
      {getPublicRoutes(Layout, RootPage)}
      {getDiscoverRoutes(Layout)}
      {getMemberRoutes(Layout, ProtectedRoute)}
      {getAdminRoutes(Layout, ProtectedRoute, VBBProtectedRoute)}

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </FeatureToggleProvider>
    </ClubBrandingProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <PublicAccessProvider>
        <PetContextProvider>
          <PrivacyProvider>
            <LanguageProvider>
              <ClubSettingsProvider>
                <BrowserRouter>
                  <GuestIdentityProvider>
                    <SeoVerificationTags />
                    <ScriptBlocker />
                    <AppRoutes />
                    <CookieConsentBanner />
                    <BetaFeedbackButton />
                    <VersionBadge />
                    <Toaster position="bottom-right" richColors />
                  </GuestIdentityProvider>
                </BrowserRouter>
              </ClubSettingsProvider>
            </LanguageProvider>
          </PrivacyProvider>
        </PetContextProvider>
      </PublicAccessProvider>
    </AuthProvider>
  );
}

export default App;
